import { useRef, useState } from 'react';
import { shrink } from './ImagePicker';

/**
 * A photo slot small enough to sit in a table row.
 *
 * On a purchase, each piece is photographed as it is unpacked — the moment it
 * is in hand and before it goes into a showcase. So the slot is one tap: on a
 * phone or tablet it opens the camera directly, on a desktop the file picker.
 * The image is shrunk before it is kept, the same as anywhere else a photo is
 * taken, so a 5 MB phone picture does not end up in every row of the database.
 */
export function PhotoCell({
  value,
  onChange,
  size = 'h-12 w-12',
}: {
  value: string | null;
  onChange: (dataUri: string | null) => void;
  size?: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const pick = async (file: File): Promise<void> => {
    setFailed(false);
    if (!file.type.startsWith('image/')) {
      setFailed(true);
      return;
    }
    setBusy(true);
    try {
      onChange(await shrink(file));
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative">
      <input
        ref={input}
        type="file"
        accept="image/*"
        // Opens the rear camera on a phone or tablet; a file picker elsewhere.
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void pick(f);
          e.target.value = '';
        }}
      />
      <button
        type="button"
        title={value ? 'Change photo' : 'Take or choose a photo'}
        onClick={() => input.current?.click()}
        className={`${size} grid place-items-center overflow-hidden rounded-md border-2 border-dashed transition-colors ${
          value ? 'border-gold-400 border-solid' : failed ? 'border-danger' : 'border-silver-400/60 hover:border-gold-500'
        } bg-silver-50`}
      >
        {busy ? (
          <span className="text-2xs text-ink-faint">…</span>
        ) : value ? (
          <img src={value} alt="" className="h-full w-full object-cover" />
        ) : (
          <svg viewBox="0 0 20 20" className="h-5 w-5 text-ink-faint" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <path d="M3 6.5h3l1.5-2h5l1.5 2h3v9H3z" strokeLinejoin="round" />
            <circle cx="10" cy="10.5" r="2.8" />
          </svg>
        )}
      </button>
      {value ? (
        <button
          type="button"
          title="Remove photo"
          onClick={() => onChange(null)}
          className="absolute -right-1.5 -top-1.5 grid h-4 w-4 place-items-center rounded-full bg-danger text-2xs leading-none text-white"
        >
          ×
        </button>
      ) : null}
    </div>
  );
}
